import React, { useState, useMemo } from 'react'
import { Grid, Switch, FormControlLabel } from '@the-platform-group/component-library'
import { Button } from '@the-platform-group/component-library/Button'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import JobLabel from './JobLabel'
import JobMapHeader from './JobMapHeader'
import { findJobsBySearchTerm } from './utils'
import JobsListWrapper, { NoJobsAvailable, NoJobsMatchSearch } from './JobsListWrapper'

/**
 * FactSetJobsList - Displays list of FactSet jobs (target profile jobs) during job mapping
 * Shows all FactSet jobs from the target profile, categorized as:
 * Mapped Jobs: Jobs that already have Platform jobs mapped to them (with "View" button)
 * Unmapped Jobs: Jobs available for mapping
 *
 * @param {Object} props
 * @param {Function} props.openJobMappingPreview - Callback to open JobMappedPreviewDrawer with job ID
 *
 * @returns {React.Component} List of FactSet jobs with search and filter controls
 */
const FactSetJobsList = ({ openJobMappingPreview }) => {
  const { mergeData, jobMappingData } = usePeopleMergeContext()
  const { targetJobs, previewJobsMap } = mergeData
  const { mappedTargetJobIds } = jobMappingData

  const [searchTerm, setSearchTerm] = useState('')
  const [showInactiveJobs, setShowInactiveJobs] = useState(false)

  const filteredMappedJobs = useMemo(() => {
    const mappedJobs = targetJobs.filter(job => mappedTargetJobIds.includes(job.id))

    return findJobsBySearchTerm(mappedJobs, searchTerm)
  }, [targetJobs, mappedTargetJobIds, searchTerm])

  const filteredUnmappedJobs = useMemo(() => {
    let unmappedJobs = targetJobs.filter(job => !mappedTargetJobIds.includes(job.id))

    if (!showInactiveJobs) {
      unmappedJobs = unmappedJobs.filter(job => job.active !== false)
    }

    return findJobsBySearchTerm(unmappedJobs, searchTerm)
  }, [targetJobs, mappedTargetJobIds, searchTerm, showInactiveJobs])

  const hasJobs = targetJobs.length > 0
  const hasFilteredJobs = filteredMappedJobs.length > 0 || filteredUnmappedJobs.length > 0

  const inactiveJobsSwitch = (
    <FormControlLabel
      control={
        <Switch
          checked={showInactiveJobs}
          onChange={(_, value) => setShowInactiveJobs(value)}
          size="small"
          color="primary"
        />
      }
      label="View inactive jobs"
      labelPlacement="start"
      sx={{ mr: 0 }}
    />
  )

  return (
    <JobsListWrapper
      title="FactSet Jobs"
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      action={inactiveJobsSwitch}
    >
      <Grid item container sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {!hasJobs && !hasFilteredJobs && <NoJobsAvailable />}
        {hasJobs && !hasFilteredJobs && <NoJobsMatchSearch />}
        {hasJobs && hasFilteredJobs && (
          <Grid item container>
            {filteredMappedJobs.length > 0 && (
              <Grid container item>
                <Grid item xs={12}>
                  <JobMapHeader title="Mapped Jobs" />
                </Grid>
                {filteredMappedJobs.map(job => (
                  <Grid
                    item
                    container
                    key={job.id}
                    wrap="nowrap"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Grid item sx={{ flexGrow: 1, minWidth: 0, pl: 2 }}>
                      <JobLabel job={job} />
                    </Grid>
                    <Grid item sx={{ flexShrink: 0 }}>
                      <Button
                        variant="default"
                        size="small"
                        onClick={() => openJobMappingPreview(job.id)}
                      >
                        View
                      </Button>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}

            {filteredUnmappedJobs.length > 0 && (
              <Grid
                item
                xs={12}
                sx={{
                  mt: filteredMappedJobs.length > 0 ? 1 : 0,
                  pt: filteredMappedJobs.length > 0 ? 2 : 0,
                  borderTop: filteredMappedJobs.length > 0 ? '2px solid' : 'none',
                  borderColor: 'border.interactive',
                }}
              >
                {filteredMappedJobs.length > 0 && (
                  <Grid item xs={12}>
                    <JobMapHeader title="Unmapped Jobs" />
                  </Grid>
                )}
                {filteredUnmappedJobs.map(job => (
                  <Grid
                    item
                    container
                    key={job.id}
                    wrap="nowrap"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Grid item sx={{ pl: 2 }}>
                      <JobLabel job={job} jobPreview={previewJobsMap.get(job.id)} />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </JobsListWrapper>
  )
}

export default FactSetJobsList
